import i18n from "@/i18n";
/**
 * WORKFORCE-EXTERNAL-EXTRAS-QR-V1 — manager page: restaurant QR, extra
 * shift reports review, and the worked-hours source (manual → automatic).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { canManageAbsences } from "@/components/workforce/absenceShared";
import { formatYMDDate } from "@/components/workforce/workforceHelpers";
import { ExternalExtrasQrCard } from "@/components/workforce/ExternalExtrasQrCard";
import { ExternalExtrasReviewDialog } from "@/components/workforce/ExternalExtrasReviewDialog";
import {
    EXTRA_STATUS_LABEL, HOURS_REASON_TEXT, extraErrorMessage, fmtHours, formatMinutes, hoursTotals,
} from "@/components/workforce/externalExtrasHelpers";

const ymd = (d) => d.toISOString().slice(0, 10);

function coverageLines(days) {
    const by = {};
    days.forEach((d) => d.reasons.forEach((r) => { (by[r] = by[r] || []).push(d.date); }));
    return Object.entries(by).map(([r, ds]) => `${HOURS_REASON_TEXT[r]}: ${ds.length} day(s)`
        + ` (${ds.slice(0, 3).map(formatYMDDate).join(", ")}${ds.length > 3 ? " …" : ""})`).join("; ");
}

function HoursSource({ restaurantId, range, reloadKey }) {
    const [src, setSrc] = useState(null);
    const [from, setFrom] = useState("");
    const [reason, setReason] = useState("");
    const load = useCallback(() => {
        api.get("/workforce/hours-source", { params: { restaurant_id: restaurantId, ...range } })
            .then(({ data }) => setSrc(data)).catch(() => setSrc(null));
    }, [restaurantId, range]);
    useEffect(() => { if (restaurantId) load(); }, [restaurantId, load, reloadKey]);
    const save = async (value) => {
        try {
            await api.post("/workforce/hours-source", { restaurant_id: restaurantId, automatic_from: value, reason });
            toast.success(value ? i18n.t("wf.workforceExternalExtras.automaticWorkedHoursScheduled", "Automatic worked hours scheduled") : i18n.t("wf.workforceExternalExtras.manualWorkedHoursRestored", "Manual worked hours restored"));
            setReason("");
            load();
        } catch (e) {
            toast.error(extraErrorMessage(e, "Could not save."));
        }
    };
    if (!src) return null;
    const t = hoursTotals(src.days);
    return (
        <section className="rounded-xl border border-neutral-200 bg-white p-5" data-testid="hours-source-card">
            <h2 className="text-base font-semibold text-neutral-900">{i18n.t("wf.workforceExternalExtras.workedHoursForReportsAndTeho", "Worked hours for Reports and Teho")}</h2>
            <p className="mt-1 text-sm text-neutral-600" data-testid="hours-source-mode">
                {src.automatic_from
                    ? `Automatic from ${formatYMDDate(src.automatic_from)}: confirmed employee hours + approved extra reports. Earlier days keep their manual Daily Performance hours.`
                    : i18n.t("wf.workforceExternalExtras.manualDailyPerformanceWorkedHoursAre", "Manual: Daily Performance worked hours are used. Nothing is calculated automatically yet.")}
            </p>
            <dl className="mt-3 grid grid-cols-3 gap-3 text-sm" data-testid="hours-source-totals">
                <div><dt className="text-neutral-500">{i18n.t("wf.workforceExternalExtras.employeeHours", "Employee hours")}</dt>
                    <dd data-testid="hours-source-employee">{fmtHours(t.employeeHours)}</dd></div>
                <div><dt className="text-neutral-500">{i18n.t("wf.workforceExternalExtras.externalExtraHours", "External extra hours")}</dt>
                    <dd data-testid="hours-source-external">{fmtHours(t.externalHours)}</dd></div>
                <div><dt className="text-neutral-500">{i18n.t("wf.workforceExternalExtras.total", "Total")}</dt>
                    <dd data-testid="hours-source-total">{fmtHours(t.totalHours)}</dd></div>
            </dl>
            <p className="mt-2 text-xs text-neutral-600" data-testid="hours-source-coverage">
                {t.incomplete.length
                    ? `Not complete for ${t.incomplete.length} day(s) — Teho for this range is not final. ${coverageLines(t.incomplete)}`
                    : i18n.t("wf.workforceExternalExtras.allDaysInThisRangeAre", "All days in this range are confirmed.")}
            </p>
            <div className="mt-4 flex flex-wrap items-end gap-2">
                <Input type="date" className="w-44" value={from} data-testid="hours-source-from"
                    onChange={(e) => setFrom(e.target.value)} />
                <Input className="w-64" placeholder={i18n.t("wf.workforceExternalExtras.reasonRequired", "Reason (required)")} value={reason}
                    data-testid="hours-source-reason" onChange={(e) => setReason(e.target.value)} />
                <Button size="sm" disabled={!from} data-testid="hours-source-activate" onClick={() => save(from)}>
                    {i18n.t("wf.workforceExternalExtras.useAutomaticHoursFromThisDate", "Use automatic hours from this date")}
                </Button>
                {src.automatic_from && (
                    <Button size="sm" variant="outline" data-testid="hours-source-manual" onClick={() => save(null)}>
                        {i18n.t("wf.workforceExternalExtras.backToManual", "Back to manual")}
                    </Button>
                )}
            </div>
        </section>
    );
}

export default function WorkforceExternalExtras() {
    const { restaurantId, currentRestaurant, user } = useApp();
    const canManage = canManageAbsences(user);
    const [range, setRange] = useState(() => {
        const to = new Date();
        return { date_from: ymd(new Date(to.getTime() - 13 * 864e5)), date_to: ymd(to) };
    });
    const [reports, setReports] = useState([]);
    const [open, setOpen] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const load = useCallback(() => {
        if (!restaurantId) return;
        api.get("/workforce/external-extras/reports", { params: { restaurant_id: restaurantId, ...range } })
            .then(({ data }) => setReports(data.reports))
            .catch((e) => toast.error(extraErrorMessage(e, "Could not load reports.")));
    }, [restaurantId, range]);
    useEffect(() => { load(); }, [load]);
    const saved = (doc) => {
        setReports((rs) => rs.map((r) => (r.report_id === doc.report_id ? doc : r)));
        setOpen(doc);
        setReloadKey((k) => k + 1);
    };
    return (
        <div className="space-y-6" data-testid="workforce-external-extras">
            <PageHeader title={i18n.t("wf.workforceExternalExtras.externalExtras", "External extras")} subtitle={i18n.t("wf.workforceExternalExtras.agencyExtrasReportTheirOwnShifts", "Agency extras report their own shifts with the restaurant QR.")} />
            <ExternalExtrasQrCard restaurantId={restaurantId} restaurantName={currentRestaurant?.name} canManage={canManage} />
            <div className="flex flex-wrap items-center gap-2">
                <Input type="date" className="w-44" value={range.date_from} data-testid="extras-range-from"
                    onChange={(e) => setRange((r) => ({ ...r, date_from: e.target.value }))} />
                <span className="text-neutral-500">–</span>
                <Input type="date" className="w-44" value={range.date_to} data-testid="extras-range-to"
                    onChange={(e) => setRange((r) => ({ ...r, date_to: e.target.value }))} />
            </div>
            <HoursSource restaurantId={restaurantId} range={range} reloadKey={reloadKey} />
            <section className="rounded-xl border border-neutral-200 bg-white" data-testid="extras-reports">
                {reports.length === 0 && <p className="p-5 text-sm text-neutral-600" data-testid="extras-reports-empty">
                    No extra shift reports in this range.</p>}
                <ul className="divide-y divide-neutral-100">
                    {reports.map((r) => (
                        <li key={r.report_id}>
                            <button type="button" onClick={() => setOpen(r)} data-testid={`extras-report-${r.report_id}`}
                                className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left hover:bg-neutral-50">
                                <span className="text-sm">
                                    <span className="font-medium text-neutral-900">{r.current.name}</span>
                                    <span className="text-neutral-600"> · {r.current.agency} · {formatYMDDate(r.current.shift_date)} {r.current.start_time}–{r.current.end_time} · {formatMinutes(r.current.duration_minutes)}</span>
                                </span>
                                <Badge variant={r.status === "APPROVED" ? "default" : "outline"}
                                    data-testid={`extras-report-status-${r.report_id}`}>{EXTRA_STATUS_LABEL[r.status]}</Badge>
                            </button>
                        </li>
                    ))}
                </ul>
            </section>
            <ExternalExtrasReviewDialog report={open} restaurantId={restaurantId} canManage={canManage}
                onClose={() => setOpen(null)} onSaved={saved} />
        </div>
    );
}
